/**
 * Social Proof Configuration for Minute7 Signup Page
 *
 * IMPORTANT: All claims in this file are based on verified data from missioncritical.llc
 * and must be kept accurate. Update this file when metrics change.
 *
 * Last Updated: 2025-10-15
 */

export const STATS = {
    activeBusinesses: {
        value: '3,000+',
        label: 'Active Businesses',
        verified: true,
        source: 'Mission Critical SaaS portfolio',
    },
    capterra: {
        rating: 4.4,
        reviews: 300,
        maxRating: 5,
        platform: 'Capterra',
        verified: true,
        source: 'Capterra profile',
    },
    uptime: {
        value: '99.9%',
        label: 'Uptime SLA',
        verified: true,
        source: 'Infrastructure monitoring',
    },
};

export const TESTIMONIALS = [
    {
        id: 'sarah-martinez',
        quote:
            "I used to spend 6 hours every Friday on timesheets and QuickBooks. Now I spend 30 minutes—and everything's already synced.",
        authorName: 'Sarah Martinez',
        authorTitle: 'Principal',
        authorCompany: 'Martinez Consulting',
        featured: true,
        verified: true,
        // No photo - using initials style
        initials: 'SM',
    },
    {
        id: 'connie-farino',
        quote:
            "I've been using Minute7 for 5 years. I would never consider anything else. It is hands down the best time and expense tracking software",
        authorName: 'Connie Farino',
        authorTitle: '',
        authorCompany: 'Westside Financial',
        featured: false,
        verified: true,
        initials: 'CF',
    },
    {
        id: 'jon-bowman',
        quote:
            'Minute7 has really simplified my payroll and reimbursements. Super easy integration with QuickBooks!',
        authorName: 'Jon Bowman',
        authorTitle: '',
        authorCompany: 'Concord Custom Cleaners',
        featured: false,
        verified: true,
        initials: 'JB',
    },
];

export const VALUE_PROPS = {
    primary: 'Stop Losing Billable Hours',
    secondary: 'Automatically track time and expenses with QuickBooks integration',
    recovery: {
        amount: '$2,000+',
        description: 'Average recoverable billable hours in first month',
        verified: true,
    },
};

export const FEATURES = [
    {
        id: 'qb-integration',
        icon: 'Link',
        title: 'Direct QuickBooks Integration',
        description: 'Seamlessly sync with QuickBooks Online and Desktop',
    },
    {
        id: 'mobile-apps',
        icon: 'Smartphone',
        title: 'Mobile Apps',
        description: 'Track time on-the-go with iOS and Android apps',
    },
    {
        id: 'expense-tracking',
        icon: 'DollarSign',
        title: 'Expense Tracking',
        description: 'Built-in mileage calculator and receipt capture',
    },
    {
        id: 'pricing',
        icon: 'Tag',
        title: 'Simple Pricing',
        description: '$8 per user per month - no hidden fees',
    },
];

export const TRUST_BADGES = [
    {
        id: 'intuit-partner',
        label: 'Intuit Certified Partner',
        icon: 'Award',
        description: 'Official QuickBooks integration partner',
        verified: true,
    },
    {
        id: 'soc2-hosting',
        label: 'SOC 2 Type II Compliant Hosting',
        icon: 'Shield',
        description: 'Enterprise-grade security infrastructure',
        verified: true,
        // Important: This is about HOSTING, not Minute7 certification
        clarification: 'Hosted on SOC 2 Type II compliant infrastructure',
    },
    {
        id: 'ssl-secure',
        label: '256-bit SSL Encryption',
        icon: 'Lock',
        description: 'Bank-level data security',
        verified: true,
    },
    {
        id: 'support',
        label: '24/7 Global Support',
        icon: 'Headphones',
        description: 'Always here to help',
        verified: true,
    },
];

export const PRICING = {
    amount: 8,
    currency: 'USD',
    period: 'user/month',
    trial: {
        available: true,
        duration: 'Free trial',
        noCreditCard: true,
    },
};

// Helper function to get testimonial by ID
export const getTestimonialById = id => {
    return TESTIMONIALS.find(t => t.id === id);
};

// Helper function to get featured testimonial
export const getFeaturedTestimonial = () => {
    return TESTIMONIALS.find(t => t.featured) || TESTIMONIALS[0];
};

// Validation: Ensure all required data is present
if (process.env.NODE_ENV === 'development') {
    const validateConfig = () => {
        const errors = [];

        if (!STATS.activeBusinesses.verified) {
            errors.push('Active businesses stat not verified');
        }

        if (!STATS.capterra.verified) {
            errors.push('Capterra rating not verified');
        }

        if (TESTIMONIALS.length === 0) {
            errors.push('No testimonials available');
        }

        const unverifiedTestimonials = TESTIMONIALS.filter(t => !t.verified);
        if (unverifiedTestimonials.length > 0) {
            errors.push(`${unverifiedTestimonials.length} unverified testimonials`);
        }

        if (errors.length > 0) {
            console.warn('Social proof configuration warnings:', errors);
        }
    };

    validateConfig();
}
